import { useEffect, useState, useCallback, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import Badge from "../../components/ui/badge/Badge";
import { formatMoney } from "../../utils/formatMoney";
import {
  formatJourneyDistance,
  journeyRouteStops,
  journeyWaypoints,
  bookingPassengerTotal,
} from "../../utils/normalizeBookingForm";
import {
  copyDriverJobText,
  exportDriverJobSheet,
} from "../../utils/exportDriverJobSheet";
import { formatUkDateTime, formatUkPickupDateTime } from "../../utils/dateUtils";

interface BookingDirect {
  id: string;
  bookingId: number;
  jobNo: string | null;
  waitTime: string | null;
  vehicleNo: string;
  vehicleMake: string;
  vehicleModel: string;
  paymentMode: string;
  pickupDateTime: string | Date | null;
  pickupPostalCode: string;
  dropoffPostalCode: string;
  pasengerName: string;
  surName: string;
  cellno: string;
  email: string;
  vehicleTypeName: string;
  amount: number | string | null;
  status: string;
  noofPassenger: number | null;
  luggagekg: number | string | null;
  luggageDetail: string;
  childSeat: number | null;
  disableSeat: number | null;
  returnTrip: number | null;
  meetGreet: number | null;
  noOfAdult: number | null;
  noOfChild: number | null;
  arrivalFrom: string;
  flightNo: string;
  terminal: string;
  doorNo: string;
  additionalInfo: string;
  calculatedfare: number | string | null;
  estimateFareNet: number | string | null;
  manualPickupAddress: string;
  manualDropOffAddress: string;
  startpoint: string;
  startpoint1: string;
  startpoint2: string;
  startpoint3: string;
  endposition: string;
  endposition1: string;
  endposition2: string;
  endposition3: string;
  miles: number | string | null;
  distance?: number | string | null;
  paymentStatus: string;
  bookingSource: string | null;
  cancelRemarks?: string | null;
  paymentNo?: string | null;
  payment?: string | null;
  returnDate: string | Date | null;
  returnTimeStr: string;
  returnStartpoint: string;
  returnEndposition: string;
}

interface EmailMessageRecord {
  id: string;
  type: string;
  status: string;
  toAddresses: string;
  ccAddresses: string | null;
  subject: string;
  errorMessage: string | null;
  sentAt: string | null;
  createdAt: string;
}

interface BookingEmailsResponse {
  messages: EmailMessageRecord[];
  hasUnsent: boolean;
  integrations?: {
    trustpilotInvite?: boolean;
  };
}

function formatEmailType(type: string): string {
  switch (type) {
    case "booking_confirmation":
      return "Confirmation";
    case "journey_details":
      return "Journey details";
    case "admin_relay":
      return "Admin copy";
    case "trustpilot_review_invite":
      return "Trustpilot review invite";
    default:
      return type.replace(/_/g, " ");
  }
}

function emailStatusBadgeColor(
  status: string,
): "success" | "warning" | "error" | "info" | "light" {
  switch (status.toLowerCase()) {
    case "sent":
      return "success";
    case "relayed_to_admin":
      return "info";
    case "pending":
      return "warning";
    case "failed":
      return "error";
    default:
      return "light";
  }
}

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value instanceof Date) return formatUkDateTime(value);
  return String(value);
}

function formatPickupDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  return formatUkPickupDateTime(value);
}

function bookingReference(booking: BookingDirect): string {
  if (booking.bookingId > 0) return `#${booking.bookingId}`;
  return booking.id.slice(0, 8).toUpperCase();
}

function statusBadgeColor(status: string): "success" | "warning" | "error" | "info" | "light" {
  const key = status.toLowerCase();
  if (["completed", "confirmed", "paid", "in_progress"].includes(key)) return "success";
  if (["pending"].includes(key)) return "warning";
  if (["cancelled", "failed", "refunded"].includes(key)) return "error";
  return "light";
}

function formatStatusLabel(status: string): string {
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function DetailField({
  label,
  value,
  className = "",
}: {
  label: string;
  value: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="mt-1.5 text-sm font-medium text-gray-900 dark:text-white/90">{value}</dd>
    </div>
  );
}

function SectionCard({
  title,
  children,
  className = "",
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6 ${className}`}
    >
      <h3 className="mb-5 text-base font-semibold text-gray-900 dark:text-white/90">{title}</h3>
      {children}
    </section>
  );
}

function ServiceTag({ active, label }: { active: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
        active
          ? "bg-brand-50 text-brand-700 ring-1 ring-brand-200 dark:bg-brand-500/15 dark:text-brand-300 dark:ring-brand-500/30"
          : "bg-gray-100 text-gray-500 ring-1 ring-gray-200 dark:bg-white/5 dark:text-gray-400 dark:ring-gray-700"
      }`}
    >
      {label}
    </span>
  );
}

function RouteStopCard({
  label,
  address,
  postcode,
  meta,
}: {
  label: string;
  address: string;
  postcode?: string;
  meta?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-white/[0.02]">
      <p className="text-xs font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-400">
        {label}
      </p>
      <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white/90">{address}</p>
      {postcode ? (
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{postcode}</p>
      ) : null}
      {meta ? <div className="mt-3 text-sm text-gray-600 dark:text-gray-300">{meta}</div> : null}
    </div>
  );
}

function RouteArrow() {
  return (
    <div className="flex items-center justify-center text-gray-300 dark:text-gray-600">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M5 12H19M19 12L13 6M19 12L13 18"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

export default function BookingDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<BookingDirect | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [invoiceSending, setInvoiceSending] = useState(false);
  const [trustpilotSending, setTrustpilotSending] = useState(false);
  const [trustpilotEnabled, setTrustpilotEnabled] = useState(false);
  const [adminEmailSending, setAdminEmailSending] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailHistory, setEmailHistory] = useState<EmailMessageRecord[]>([]);
  const [hasUnsentEmail, setHasUnsentEmail] = useState(false);
  const [exportError, setExportError] = useState("");
  const [copyMessage, setCopyMessage] = useState("");
  const [copyError, setCopyError] = useState("");
  const [copying, setCopying] = useState(false);

  const fetchEmailHistory = useCallback(async () => {
    if (!id) return;
    try {
      const response = await axiosInstance.get<BookingEmailsResponse>(
        `/direct-bookings/${id}/emails`,
      );
      setEmailHistory(response.data?.messages ?? []);
      setHasUnsentEmail(Boolean(response.data?.hasUnsent));
      setTrustpilotEnabled(Boolean(response.data?.integrations?.trustpilotInvite));
    } catch (fetchError) {
      console.error("Error fetching email history:", fetchError);
    }
  }, [id]);

  const fetchBookingData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/direct-bookings/${id}`);
      setBooking(response.data);
      await fetchEmailHistory();
    } catch (fetchError) {
      setError("Failed to fetch booking data");
      console.error("Error fetching booking:", fetchError);
    } finally {
      setLoading(false);
    }
  }, [id, fetchEmailHistory]);

  useEffect(() => {
    fetchBookingData();
  }, [fetchBookingData]);

  const handleSendConfirmationEmail = async () => {
    if (!id || !booking?.email?.trim()) {
      setEmailError("This booking has no customer email address.");
      return;
    }

    setEmailSending(true);
    setEmailError("");
    setEmailMessage("");

    try {
      const response = await axiosInstance.post(
        `/direct-bookings/${id}/send-confirmation-email`,
      );
      setEmailMessage(
        response.data?.message ??
          `Confirmation email sent to ${booking.email.trim()}`,
      );
      await fetchEmailHistory();
    } catch (sendError: unknown) {
      const axiosError = sendError as {
        response?: { data?: { message?: string | string[] } };
      };
      const message = axiosError.response?.data?.message;
      setEmailError(
        Array.isArray(message)
          ? message.join(", ")
          : message || "Failed to send confirmation email",
      );
      await fetchEmailHistory();
    } finally {
      setEmailSending(false);
    }
  };

  const handleSendStripeInvoice = async () => {
    if (!id || !booking?.email?.trim()) {
      setEmailError("This booking has no customer email address.");
      return;
    }

    const amountNum =
      booking.amount == null || booking.amount === ""
        ? NaN
        : Number(booking.amount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setEmailError("Set a booking amount greater than zero before sending an invoice.");
      return;
    }

    const paid = ["completed", "paid"].includes(
      (booking.paymentStatus || "").toLowerCase(),
    );
    if (paid) {
      setEmailError("This booking is already marked as paid.");
      return;
    }

    setInvoiceSending(true);
    setEmailError("");
    setEmailMessage("");

    try {
      const response = await axiosInstance.post(
        `/direct-bookings/${id}/send-stripe-invoice`,
      );
      const hostedUrl = response.data?.hostedInvoiceUrl as string | undefined;
      setEmailMessage(
        response.data?.message
          ? hostedUrl
            ? `${response.data.message} Pay link: ${hostedUrl}`
            : response.data.message
          : `Stripe invoice emailed to ${booking.email.trim()}`,
      );
      if (response.data?.booking) {
        setBooking(response.data.booking);
      } else {
        await fetchBookingData();
      }
    } catch (sendError: unknown) {
      const axiosError = sendError as {
        response?: { data?: { message?: string | string[] } };
      };
      const message = axiosError.response?.data?.message;
      setEmailError(
        Array.isArray(message)
          ? message.join(", ")
          : message || "Failed to send Stripe invoice",
      );
    } finally {
      setInvoiceSending(false);
    }
  };

  const handleSendTrustpilotReviewInvite = async () => {
    if (!id || !booking?.email?.trim()) {
      setEmailError("This booking has no customer email address.");
      return;
    }

    setTrustpilotSending(true);
    setEmailError("");
    setEmailMessage("");

    try {
      const response = await axiosInstance.post(
        `/direct-bookings/${id}/send-trustpilot-review-invite`,
      );
      setEmailMessage(
        response.data?.message ??
          `Trustpilot review invitation queued for ${booking.email.trim()}`,
      );
      await fetchEmailHistory();
    } catch (sendError: unknown) {
      const axiosError = sendError as {
        response?: { data?: { message?: string | string[] } };
      };
      const message = axiosError.response?.data?.message;
      setEmailError(
        Array.isArray(message)
          ? message.join(", ")
          : message || "Failed to send Trustpilot review invitation",
      );
      await fetchEmailHistory();
    } finally {
      setTrustpilotSending(false);
    }
  };

  const handleSendEmailToAdmin = async () => {
    if (!id) return;

    setAdminEmailSending(true);
    setEmailError("");
    setEmailMessage("");

    try {
      const response = await axiosInstance.post(
        `/direct-bookings/${id}/send-email-to-admin`,
      );
      setEmailMessage(
        response.data?.message ?? "Email sent to admin successfully",
      );
      await fetchEmailHistory();
    } catch (sendError: unknown) {
      const axiosError = sendError as {
        response?: { data?: { message?: string | string[] } };
      };
      const message = axiosError.response?.data?.message;
      setEmailError(
        Array.isArray(message)
          ? message.join(", ")
          : message || "Failed to send email to admin",
      );
      await fetchEmailHistory();
    } finally {
      setAdminEmailSending(false);
    }
  };

  const handleCopyJobForDriver = async () => {
    if (!booking) return;
    setCopying(true);
    setCopyError("");
    setCopyMessage("");

    try {
      await copyDriverJobText(booking);
      setCopyMessage("Job details copied — ready to paste for your driver.");
    } catch (err) {
      setCopyError(
        err instanceof Error ? err.message : "Failed to copy job details",
      );
    } finally {
      setCopying(false);
    }
  };

  const handleExportJobDetails = () => {
    if (!booking) return;
    setExportError("");
    try {
      exportDriverJobSheet(booking);
    } catch (err) {
      setExportError(
        err instanceof Error ? err.message : "Failed to export job details",
      );
    }
  };


  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-600 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-400">
        {error}
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-4 text-yellow-700 dark:border-yellow-900/40 dark:bg-yellow-900/20 dark:text-yellow-300">
        Booking not found
      </div>
    );
  }

  const amount = formatMoney(booking.amount, "£");
  const viaStops = journeyWaypoints(booking);
  const routeStops = journeyRouteStops(booking);
  const journeyDistance = formatJourneyDistance(booking);
  const passengerFullName = [booking.pasengerName, booking.surName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  const noteFields = [
    booking.jobNo?.trim() ? { label: "Job Number", value: booking.jobNo.trim() } : null,
    booking.additionalInfo?.trim()
      ? { label: "Customer Notes", value: booking.additionalInfo.trim() }
      : null,
    booking.waitTime?.trim()
      ? { label: "Wait Instructions", value: booking.waitTime.trim() }
      : null,
    booking.luggageDetail?.trim()
      ? { label: "Luggage Details", value: booking.luggageDetail.trim() }
      : null,
    booking.cancelRemarks?.trim()
      ? { label: "Cancellation Remarks", value: booking.cancelRemarks.trim() }
      : null,
  ].filter((entry): entry is { label: string; value: string } => Boolean(entry));

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <PageBreadcrumb pageTitle="Booking Details" />
        <div className="flex shrink-0 flex-wrap gap-2">
          <button
            type="button"
            onClick={handleCopyJobForDriver}
            disabled={copying}
            className="rounded-lg border border-brand-200 bg-brand-50 px-4 py-2 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300"
          >
            {copying ? "Copying..." : "Copy for Driver"}
          </button>
          <button
            type="button"
            onClick={handleExportJobDetails}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Export Job Details
          </button>
          <button
            type="button"
            onClick={handleSendConfirmationEmail}
            disabled={emailSending || !booking.email?.trim()}
            className="rounded-lg border border-brand-200 bg-brand-50 px-4 py-2 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300"
          >
            {emailSending ? "Sending..." : "Send Email"}
          </button>
          <button
            type="button"
            onClick={handleSendStripeInvoice}
            disabled={
              invoiceSending ||
              !booking.email?.trim() ||
              ["completed", "paid"].includes(
                (booking.paymentStatus || "").toLowerCase(),
              )
            }
            className="rounded-lg border border-indigo-300 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-800 transition-colors hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-indigo-300"
            title="Emails the customer a Stripe invoice with a card payment link"
          >
            {invoiceSending ? "Sending invoice..." : "Email Stripe Invoice"}
          </button>
          {trustpilotEnabled ? (
            <button
              type="button"
              onClick={handleSendTrustpilotReviewInvite}
              disabled={trustpilotSending || !booking.email?.trim()}
              className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-800 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300"
              title="Emails the customer with Trustpilot BCC so Trustpilot sends a review invitation"
            >
              {trustpilotSending ? "Sending..." : "Request Trustpilot Review"}
            </button>
          ) : null}
          {hasUnsentEmail ? (
            <button
              type="button"
              onClick={handleSendEmailToAdmin}
              disabled={adminEmailSending}
              className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
            >
              {adminEmailSending ? "Sending..." : "Send to Admin"}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => navigate(`/bookings/edit/${id}`)}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
          >
            Edit Booking
          </button>
          <button
            type="button"
            onClick={() => navigate("/bookings")}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Back to List
          </button>
        </div>
      </div>

      {emailMessage && (
        <div className="rounded-xl border border-success-200 bg-success-50 px-4 py-3 text-sm text-success-700 dark:border-success-500/30 dark:bg-success-500/10 dark:text-success-400">
          {emailMessage}
        </div>
      )}
      {emailError && (
        <div className="rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
          {emailError}
        </div>
      )}
      {exportError && (
        <div className="rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
          {exportError}
        </div>
      )}
      {copyMessage && (
        <div className="rounded-xl border border-success-200 bg-success-50 px-4 py-3 text-sm text-success-700 dark:border-success-500/30 dark:bg-success-500/10 dark:text-success-400">
          {copyMessage}
        </div>
      )}
      {copyError && (
        <div className="rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
          {copyError}
        </div>
      )}

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br from-brand-50 via-white to-white dark:border-gray-800 dark:from-brand-500/10 dark:via-gray-900 dark:to-gray-900">
        <div className="flex flex-col gap-6 p-6 lg:flex-row lg:items-center lg:justify-between lg:p-8">
          <div className="space-y-3">
            <p className="text-sm font-medium text-brand-600 dark:text-brand-400">
              Booking {bookingReference(booking)}
            </p>
            <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white/90 sm:text-3xl">
              {displayValue(passengerFullName || booking.pasengerName)}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {formatPickupDate(booking.pickupDateTime)}
              {booking.vehicleTypeName ? ` · ${booking.vehicleTypeName}` : ""}
              {journeyDistance !== "—" ? ` · ${journeyDistance}` : ""}
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Badge color={statusBadgeColor(booking.status)} size="sm">
                {formatStatusLabel(booking.status)}
              </Badge>
              <Badge color={statusBadgeColor(booking.paymentStatus)} size="sm">
                Payment {formatStatusLabel(booking.paymentStatus)}
              </Badge>
              {booking.paymentMode ? (
                <Badge color="info" size="sm">
                  {formatStatusLabel(booking.paymentMode)}
                </Badge>
              ) : null}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white/80 px-6 py-5 text-center shadow-theme-sm backdrop-blur dark:border-gray-700 dark:bg-gray-900/80 lg:min-w-[200px]">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Total Amount
            </p>
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white/90">{amount}</p>
            {booking.calculatedfare != null ? (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Fare {formatMoney(booking.calculatedfare, "£")}
              </p>
            ) : null}
          </div>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <SectionCard title="Passenger">
          <dl className="grid gap-5 sm:grid-cols-2">
            <DetailField label="Name" value={displayValue(booking.pasengerName)} />
            <DetailField label="Phone" value={displayValue(booking.cellno)} />
            <DetailField label="Email" value={displayValue(booking.email)} className="sm:col-span-2" />
            <DetailField label="Adults" value={displayValue(booking.noOfAdult)} />
            <DetailField label="Children" value={displayValue(booking.noOfChild)} />
            <DetailField label="Total Passengers" value={displayValue(bookingPassengerTotal(booking))} />
            <DetailField label="Luggage (kg)" value={displayValue(booking.luggagekg)} />
          </dl>
        </SectionCard>

        <SectionCard title="Payment">
          <dl className="grid gap-5 sm:grid-cols-2">
            <DetailField label="Amount" value={amount} />
            <DetailField label="Calculated Fare" value={formatMoney(booking.calculatedfare, "£")} />
            <DetailField
              label="Estimated Net Fare"
              value={formatMoney(booking.estimateFareNet, "£")}
            />
            <DetailField label="Payment Mode" value={formatStatusLabel(booking.paymentMode || "—")} />
            <DetailField
              label="Payment Status"
              value={
                <Badge color={statusBadgeColor(booking.paymentStatus)} size="sm">
                  {formatStatusLabel(booking.paymentStatus)}
                </Badge>
              }
            />
            <DetailField
              label="Booking Status"
              value={
                <Badge color={statusBadgeColor(booking.status)} size="sm">
                  {formatStatusLabel(booking.status)}
                </Badge>
              }
            />
            <DetailField label="Website Source" value={displayValue(booking.bookingSource)} />
            {booking.paymentNo ? (
              <DetailField
                label="Payment / Invoice Ref"
                value={displayValue(booking.paymentNo)}
                className="sm:col-span-2"
              />
            ) : null}
            {booking.payment?.startsWith("http") ? (
              <DetailField
                label="Invoice pay link"
                value={
                  <a
                    href={booking.payment}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all text-brand-600 underline hover:text-brand-700 dark:text-brand-400"
                  >
                    Open hosted invoice
                  </a>
                }
                className="sm:col-span-2"
              />
            ) : null}
          </dl>
        </SectionCard>

        <SectionCard title="Journey" className="lg:col-span-2">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {viaStops.length
                ? `${viaStops.length} via stop${viaStops.length === 1 ? "" : "s"}`
                : "Direct route"}
            </p>
            <span className="inline-flex items-center rounded-full bg-brand-50 px-3 py-1 text-sm font-medium text-brand-700 ring-1 ring-brand-200 dark:bg-brand-500/15 dark:text-brand-300 dark:ring-brand-500/30">
              Distance: {journeyDistance}
            </span>
          </div>

          <div className="space-y-3">
            {routeStops.length ? (
              routeStops.map((stop, index) => (
                <div key={`${stop.kind}-${stop.viaIndex ?? index}`} className="space-y-3">
                  {index > 0 ? <RouteArrow /> : null}
                  <RouteStopCard
                    label={
                      stop.kind === "pickup"
                        ? "Pickup"
                        : stop.kind === "dropoff"
                          ? "Drop-off"
                          : `Via ${stop.viaIndex ?? index + 1}`
                    }
                    address={stop.address}
                    postcode={
                      stop.kind === "pickup"
                        ? displayValue(booking.pickupPostalCode)
                        : stop.kind === "dropoff"
                          ? displayValue(booking.dropoffPostalCode)
                          : undefined
                    }
                    meta={
                      stop.kind === "pickup"
                        ? formatPickupDate(booking.pickupDateTime)
                        : stop.kind === "dropoff" && booking.waitTime
                          ? (
                              <span>Wait time: {displayValue(booking.waitTime)}</span>
                            )
                          : undefined
                    }
                  />
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">No route details recorded.</p>
            )}
          </div>
        </SectionCard>

        <SectionCard title="Vehicle">
          <dl className="grid gap-5 sm:grid-cols-2">
            <DetailField label="Vehicle Type" value={displayValue(booking.vehicleTypeName)} />
            <DetailField label="Vehicle Number" value={displayValue(booking.vehicleNo)} />
            <DetailField label="Make" value={displayValue(booking.vehicleMake)} />
            <DetailField label="Model" value={displayValue(booking.vehicleModel)} />
          </dl>
        </SectionCard>

        <SectionCard title="Flight">
          <dl className="grid gap-5 sm:grid-cols-2">
            <DetailField label="Flight Number" value={displayValue(booking.flightNo)} />
            <DetailField label="Arrival From" value={displayValue(booking.arrivalFrom)} />
            <DetailField label="Terminal" value={displayValue(booking.terminal)} />
            <DetailField label="Door Number" value={displayValue(booking.doorNo)} />
          </dl>
        </SectionCard>

        <SectionCard title="Additional Services" className="lg:col-span-2">
          <div className="flex flex-wrap gap-2">
            <ServiceTag active={Boolean(booking.childSeat)} label="Child Seat" />
            <ServiceTag active={Boolean(booking.disableSeat)} label="Disabled Access" />
            <ServiceTag active={Boolean(booking.returnTrip)} label="Return Trip" />
            <ServiceTag active={Boolean(booking.meetGreet)} label="Meet & Greet" />
          </div>
        </SectionCard>

        <SectionCard title="Notes" className="lg:col-span-2">
          {noteFields.length ? (
            <dl className="grid gap-5">
              {noteFields.map((note) => (
                <DetailField
                  key={note.label}
                  label={note.label}
                  value={
                    <span className="whitespace-pre-wrap font-normal leading-relaxed text-gray-700 dark:text-gray-300">
                      {note.value}
                    </span>
                  }
                />
              ))}
            </dl>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">No notes for this booking.</p>
          )}
        </SectionCard>

        <SectionCard title="Email conversation" className="lg:col-span-2">
          {emailHistory.length ? (
            <div className="space-y-3">
              {emailHistory.map((mail) => (
                <div
                  key={mail.id}
                  className="rounded-xl border border-gray-200 px-4 py-3 dark:border-gray-700"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white/90">
                        {formatEmailType(mail.type)}
                      </p>
                      <Badge color={emailStatusBadgeColor(mail.status)} size="sm">
                        {formatStatusLabel(mail.status)}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatPickupDate(mail.sentAt ?? mail.createdAt)}
                    </p>
                  </div>
                  <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                    {mail.subject}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    To: {mail.toAddresses || "(none)"}
                    {mail.ccAddresses ? ` · Cc: ${mail.ccAddresses}` : ""}
                  </p>
                  {mail.errorMessage ? (
                    <p className="mt-2 text-xs text-error-600 dark:text-error-400">
                      {mail.errorMessage}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No emails recorded for this booking yet.
            </p>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
